// src/utils/pdfGenerator.js
// Utilidad para generar PDFs imprimibles usando ventanas del navegador
import { calculateAge } from './formatters';

const PDF_STYLES = `
  /* ============================================
     CopaGimnasta — Reportes Pro Sport
     Paleta: deep navy + electric purple + gold
     ============================================ */
  * { margin: 0; padding: 0; box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }
  @media print {
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
    }
    html, body { background: #fff !important; }
    /* Hero header sin gradiente en impresión (mantener estilo sobrio del anterior) */
    .pdf-hero {
      background: #fff !important;
      color: #0f172a !important;
      border-bottom: 2px solid #e2e8f0 !important;
      padding: 14px 20px !important;
    }
    .pdf-hero::before, .pdf-hero::after { display: none !important; }
    .pdf-hero-title { color: #0f172a !important; }
    .pdf-hero-eyebrow { color: #64748b !important; }
    .pdf-hero-subtitle { color: #475569 !important; }
    .pdf-footer-accent { display: none !important; }
  }
  body {
    font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
    color: #0f172a;
    padding: 0;
    background: #fff;
    font-feature-settings: 'kern','liga','cv02','cv11';
  }

  /* ===== Encabezado sport (suave) ===== */
  .pdf-hero {
    position: relative;
    background: linear-gradient(135deg, #334155 0%, #3b4668 50%, #475569 100%);
    color: #fff;
    padding: 22px 28px;
    overflow: hidden;
    margin-bottom: 0;
  }
  .pdf-hero::before {
    content: '';
    position: absolute;
    top: -40%;
    right: -15%;
    width: 260px;
    height: 260px;
    background: radial-gradient(circle, rgba(165,180,252,0.12) 0%, rgba(165,180,252,0) 70%);
    pointer-events: none;
  }
  .pdf-hero::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #818cf8 0%, #a78bfa 50%, #c4b5fd 100%);
  }
  .pdf-hero-inner {
    position: relative;
    display: flex;
    align-items: center;
    gap: 18px;
    z-index: 1;
  }
  .pdf-hero-logo {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    object-fit: cover;
    border: 3px solid rgba(255,255,255,0.25);
    background: rgba(255,255,255,0.1);
    flex-shrink: 0;
    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
  }
  .pdf-hero-logo-placeholder {
    width: 64px;
    height: 64px;
    border-radius: 14px;
    background: rgba(255,255,255,0.15);
    border: 3px solid rgba(255,255,255,0.25);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 800;
    font-size: 1.6rem;
    color: #fff;
    flex-shrink: 0;
  }
  .pdf-hero-meta { flex: 1; min-width: 0; }
  .pdf-hero-eyebrow {
    display: inline-block;
    padding: 3px 10px;
    background: rgba(255,255,255,0.15);
    color: #e0e7ff;
    border-radius: 4px;
    font-size: 0.62rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 1.2px;
    margin-bottom: 6px;
  }
  .pdf-hero-title {
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.02em;
    line-height: 1.15;
    color: #fff;
    margin-bottom: 3px;
  }
  .pdf-hero-subtitle {
    font-size: 0.82rem;
    color: rgba(255,255,255,0.8);
    font-weight: 500;
  }
  .pdf-hero-meta-extra {
    display: inline-block;
    margin-top: 4px;
    font-size: 0.72rem;
    color: #c4b5fd;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .pdf-hero-brand {
    position: absolute;
    top: 10px;
    right: 16px;
    font-size: 0.62rem;
    color: rgba(255,255,255,0.55);
    font-weight: 700;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    z-index: 1;
  }

  /* ===== Contenido principal ===== */
  .pdf-body { padding: 24px 28px; }

  /* ===== Sub-títulos de sección ===== */
  .pdf-subtitle {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.95rem;
    font-weight: 800;
    color: #334155;
    margin: 22px 0 12px;
    padding: 7px 14px 7px 16px;
    background: linear-gradient(90deg, #f8fafc 0%, transparent 100%);
    border-left: 3px solid #818cf8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-size: 0.8rem;
  }
  .pdf-subtitle::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, #e2e8f0 0%, transparent 100%);
    margin-left: 8px;
  }

  /* ===== Tabla moderna (clara) ===== */
  .pdf-table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 20px;
    font-size: 0.8rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    overflow: hidden;
  }
  .pdf-table th {
    background: #f1f5f9;
    color: #475569;
    font-weight: 700;
    text-align: left;
    padding: 10px 12px;
    font-size: 0.68rem;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    border-bottom: 2px solid #cbd5e1;
  }
  .pdf-table td {
    padding: 9px 12px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
    color: #334155;
  }
  .pdf-table tr:nth-child(even) td { background: #fafbfc; }
  .pdf-table tr:last-child td { border-bottom: none; }
  .pdf-table td strong { color: #1e293b; font-weight: 700; }

  /* ===== Tarjetas de stats estilo sport ===== */
  .pdf-stats {
    display: flex;
    gap: 12px;
    margin: 18px 0 22px;
    flex-wrap: wrap;
  }
  .pdf-stat {
    flex: 1;
    min-width: 130px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px 18px;
    position: relative;
    overflow: hidden;
  }
  .pdf-stat::before {
    content: '';
    position: absolute;
    top: 0; left: 0; bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, #818cf8 0%, #a78bfa 100%);
  }
  .pdf-stat-val {
    font-size: 1.7rem;
    font-weight: 800;
    color: #1e293b;
    display: block;
    line-height: 1.1;
    letter-spacing: -0.02em;
  }
  .pdf-stat-label {
    font-size: 0.65rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 700;
    margin-top: 2px;
  }

  /* ===== Badges (suaves) ===== */
  .pdf-level-badge {
    display: inline-block;
    padding: 3px 9px;
    background: #eef2ff;
    color: #4338ca;
    border-radius: 4px;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .pdf-club-badge {
    display: inline-block;
    padding: 3px 9px;
    background: #ecfeff;
    color: #155e75;
    border-radius: 4px;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
  .pdf-badge-present {
    display: inline-block;
    padding: 3px 10px;
    background: #ecfdf5;
    color: #047857;
    border-radius: 4px;
    font-size: 0.68rem;
    font-weight: 700;
  }
  .pdf-badge-absent {
    display: inline-block;
    padding: 3px 10px;
    background: #fef2f2;
    color: #b91c1c;
    border-radius: 4px;
    font-size: 0.68rem;
    font-weight: 700;
  }
  .pdf-badge-justified {
    display: inline-block;
    padding: 3px 10px;
    background: #fffbeb;
    color: #b45309;
    border-radius: 4px;
    font-size: 0.68rem;
    font-weight: 700;
  }
  .pdf-badge-none {
    display: inline-block;
    padding: 3px 10px;
    background: #f8fafc;
    color: #64748b;
    border-radius: 4px;
    font-size: 0.68rem;
    font-weight: 700;
  }

  /* ===== Rank / posición (podio) ===== */
  .pdf-rank-cell {
    text-align: center;
    font-weight: 800;
    color: #64748b;
    width: 48px;
  }
  .pdf-medal-gold { color: #d97706; font-size: 1.15rem; }
  .pdf-medal-silver { color: #64748b; font-size: 1.15rem; }
  .pdf-medal-bronze { color: #a16207; font-size: 1.15rem; }

  /* ===== Planilla de jueces ===== */
  .pdf-club-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    padding: 12px 16px;
    background: linear-gradient(135deg, #334155 0%, #3b4668 100%);
    border-radius: 10px;
    color: #fff;
  }
  .pdf-club-name { font-size: 1rem; font-weight: 800; letter-spacing: -0.01em; }
  .pdf-club-count {
    font-size: 0.72rem;
    font-weight: 800;
    color: #e0e7ff;
    background: rgba(255,255,255,0.12);
    padding: 5px 12px;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .pdf-score-table th {
    border: 1px solid #cbd5e1;
    text-align: center;
    height: 48px;
    vertical-align: bottom;
    padding-bottom: 6px;
    background: #f1f5f9 !important;
    color: #475569 !important;
  }
  .pdf-score-table td { border: 1px solid #cbd5e1; }
  .pdf-col-num { width: 36px; text-align: center; }
  .pdf-col-name { min-width: 140px; text-align: left; }
  .pdf-col-level { width: 90px; text-align: center; }
  .pdf-col-score { width: 70px; min-width: 60px; }
  .pdf-col-total {
    width: 70px;
    min-width: 60px;
    font-weight: 800 !important;
    background: #eef2ff !important;
    color: #4338ca !important;
  }
  .pdf-cell-num { text-align: center; font-weight: 700; color: #64748b; }
  .pdf-cell-name { text-align: left; font-weight: 600; }
  .pdf-cell-level { text-align: center; }
  .pdf-cell-score { height: 36px; }
  .pdf-cell-total {
    height: 36px;
    background: #f5f7ff;
    border-left: 2px solid #818cf8 !important;
  }

  .pdf-signature {
    display: flex;
    gap: 40px;
    margin-top: 36px;
    padding-top: 8px;
  }
  .pdf-signature-line {
    flex: 1;
    border-top: 1px solid #64748b;
    padding-top: 8px;
    text-align: center;
  }
  .pdf-signature-line span {
    font-size: 0.68rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 700;
  }

  /* ===== Footer (suave) ===== */
  .pdf-footer {
    margin-top: 36px;
    padding: 12px 28px;
    background: #f8fafc;
    color: #94a3b8;
    font-size: 0.65rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    letter-spacing: 0.5px;
    border-top: 1px solid #e2e8f0;
  }
  .pdf-footer strong {
    color: #6366f1;
    font-weight: 800;
    letter-spacing: 1.5px;
    text-transform: uppercase;
  }
  .pdf-footer-accent {
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #818cf8 0%, #a78bfa 50%, #c4b5fd 100%);
    margin-bottom: 0;
  }

  /* ===== Utilidades ===== */
  .page-break { page-break-before: always; margin-top: 0; }
  .pdf-empty {
    text-align: center;
    color: #94a3b8;
    padding: 48px 20px;
    font-size: 0.88rem;
    background: #f8fafc;
    border-radius: 10px;
    border: 1px dashed #cbd5e1;
    margin: 20px 0;
  }
  .pdf-group-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 18px 0 8px;
    padding: 10px 16px;
    background: #f8fafc;
    border-radius: 8px;
    border-left: 3px solid #818cf8;
    border-top: 1px solid #e2e8f0;
    border-right: 1px solid #e2e8f0;
    border-bottom: 1px solid #e2e8f0;
  }
  .pdf-group-header-title {
    flex: 1;
    font-size: 0.92rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.01em;
  }
  .pdf-group-header-count {
    font-size: 0.7rem;
    color: #64748b;
    font-weight: 600;
  }
  .pdf-group-header-pct {
    font-size: 1.2rem;
    font-weight: 800;
    letter-spacing: -0.02em;
  }

  /* ===== Fila inline: título + stat compacto a la derecha ===== */
  .pdf-block-row {
    display: flex;
    align-items: center;
    gap: 14px;
    margin: 22px 0 10px;
  }
  .pdf-block-row .pdf-subtitle {
    flex: 1;
    margin: 0;
  }
  .pdf-stat-sm {
    flex-shrink: 0;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 6px 12px;
    position: relative;
    overflow: hidden;
    min-width: 80px;
    text-align: center;
  }
  .pdf-stat-sm::before {
    content: '';
    position: absolute;
    top: 0; left: 0; bottom: 0;
    width: 3px;
    background: linear-gradient(180deg, #818cf8 0%, #a78bfa 100%);
  }
  .pdf-stat-sm-val {
    font-size: 0.95rem;
    font-weight: 800;
    color: #1e293b;
    line-height: 1;
    letter-spacing: -0.02em;
    display: block;
  }
  .pdf-stat-sm-label {
    font-size: 0.55rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-weight: 700;
    margin-top: 2px;
    display: block;
  }

  @page {
    size: A4 portrait;
    margin: 10mm;
  }

  @media print {
    body { padding: 0; }
    .pdf-hero { margin-bottom: 0; }
    .pdf-body { padding: 18px 24px; }
    .page-break { page-break-before: always; margin-top: 0; }
  }
`;

function buildHeader(event, eyebrow = 'Reporte Oficial') {
  const logoHtml = event.image_url
    ? `<img src="${event.image_url}" class="pdf-hero-logo" crossorigin="anonymous" />`
    : `<div class="pdf-hero-logo-placeholder">🏆</div>`;

  const locationLine = event.location ? ` · ${event.location}` : '';
  const disciplineLine = event.discipline
    ? `<div class="pdf-hero-meta-extra">${getDisciplineName(event.discipline)}${event.category ? ' · ' + getCategoryName(event.discipline, event.category) : ''}</div>`
    : '';

  return `
    <div class="pdf-hero">
      <span class="pdf-hero-brand">CopaGimnasta</span>
      <div class="pdf-hero-inner">
        ${logoHtml}
        <div class="pdf-hero-meta">
          <div class="pdf-hero-eyebrow">${eyebrow}</div>
          <div class="pdf-hero-title">${event.name || 'Evento'}</div>
          <div class="pdf-hero-subtitle">${event.date || ''}${locationLine}</div>
          ${disciplineLine}
        </div>
      </div>
    </div>
    <div class="pdf-body">
  `;
}

function getDisciplineName(discipline) {
  const names = {
    acrobatic: 'Gimnasia Acrobática',
    artistic: 'Gimnasia Artística',
    rhythmic: 'Gimnasia Rítmica',
    trampoline: 'Gimnasia de Trampolín',
    aerobic: 'Gimnasia Aeróbica',
    gymnastics_for_all: 'Gimnasia Para Todos',
    parkour: 'Parkour'
  };
  return names[discipline] || discipline;
}

function getCategoryName(discipline, category) {
  const cats = {
    acrobatic: { women_pair: 'Pareja Femenina', men_pair: 'Pareja Masculina', mixed_pair: 'Pareja Mixta', women_group: 'Grupo Femenino', men_group: 'Grupo Masculino' },
    artistic: { wag: 'Femenina (WAG)', mag: 'Masculina (MAG)' },
    rhythmic: { individual: 'Individual', groups: 'Conjuntos' },
    trampoline: { individual: 'Individual', synchronized: 'Sincronizado', tumbling: 'Tumbling', double_mini: 'Doble Mini Trampolín' },
    aerobic: { individual: 'Individual', mixed_pairs: 'Parejas Mixtas', trios: 'Tríos', groups: 'Grupos' },
    gymnastics_for_all: { team_gym: 'TeamGym', display: 'Exhibición' },
    parkour: { speed: 'Speed Run', freestyle: 'Freestyle' }
  };
  return cats[discipline]?.[category] || category || '';
}

function buildFooter() {
  const now = new Date();
  const dateStr = now.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return `
    </div>
    <div class="pdf-footer-accent"></div>
    <div class="pdf-footer">
      <span>Generado el ${dateStr}</span>
    </div>
  `;
}

function openPrintWindow(title, bodyHtml, options = {}) {
  const { landscape = false } = options;
  const pageOrientationCss = landscape ? '@page { size: A4 landscape; margin: 10mm; }' : '';
  // Abrimos el reporte en una nueva pestaña del navegador.
  // El usuario ve el documento renderizado y decide cuándo imprimir
  // con el botón flotante o el atajo Ctrl/Cmd + P.
  const win = window.open('', '_blank');
  if (!win) {
    alert('Por favor permite las ventanas emergentes para ver los reportes');
    return;
  }

  const toolbarStyles = `
    .pdf-toolbar {
      position: fixed;
      top: 16px;
      right: 16px;
      z-index: 9999;
      display: flex;
      gap: 8px;
      background: rgba(51, 65, 85, 0.95);
      backdrop-filter: blur(8px);
      padding: 8px;
      border-radius: 12px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.25);
    }
    .pdf-toolbar button {
      background: #6366f1;
      color: #fff;
      border: none;
      border-radius: 8px;
      padding: 10px 16px;
      font-family: 'Inter', 'Segoe UI', system-ui, sans-serif;
      font-size: 0.82rem;
      font-weight: 700;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 6px;
      transition: all 0.15s;
      letter-spacing: 0.2px;
    }
    .pdf-toolbar button:hover {
      background: #4f46e5;
      transform: translateY(-1px);
    }
    .pdf-toolbar button.pdf-toolbar-secondary {
      background: rgba(255, 255, 255, 0.1);
      color: #fff;
    }
    .pdf-toolbar button.pdf-toolbar-secondary:hover {
      background: rgba(255, 255, 255, 0.18);
    }
    @media print {
      .pdf-toolbar { display: none !important; }
    }
  `;

  const toolbarHtml = `
    <div class="pdf-toolbar no-print">
      <button onclick="window.print()" title="Imprimir o guardar como PDF (Ctrl+P)">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>
        Imprimir / Guardar PDF
      </button>
      <button class="pdf-toolbar-secondary" onclick="window.close()" title="Cerrar">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
  `;

  win.document.write(`<!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <title>${title}</title>
      <style>${PDF_STYLES}${toolbarStyles}${pageOrientationCss}</style>
    </head>
    <body>
      ${toolbarHtml}
      ${bodyHtml}
    </body>
    </html>`);
  win.document.close();
  win.focus();
}


// ===== 1. PDF DE RESULTADOS =====
export function generateResultsPDF(event, analytics) {
  if (!analytics) return;

  let html = buildHeader(event);

  // Stats
  html += `
    <div class="pdf-stats">
      <div class="pdf-stat"><span class="pdf-stat-val">${analytics.totalClubs}</span><span class="pdf-stat-label">Clubes</span></div>
      <div class="pdf-stat"><span class="pdf-stat-val">${analytics.totalGymnasts}</span><span class="pdf-stat-label">Gimnastas</span></div>
      <div class="pdf-stat"><span class="pdf-stat-val">${analytics.rankedAthletes[0]?.total || '—'}</span><span class="pdf-stat-label">Mejor Puntaje</span></div>
    </div>
  `;

  // Podium
  const medalClass = ['pdf-medal-gold', 'pdf-medal-silver', 'pdf-medal-bronze'];
  const medalIcon = ['🥇', '🥈', '🥉'];
  if (analytics.rankedAthletes.length > 0) {
    html += `<div class="pdf-subtitle">PODIO</div>`;
    html += `<table class="pdf-table"><thead><tr><th style="width:48px;text-align:center;">#</th><th>Gimnasta</th><th>Club</th><th>Nivel</th><th style="text-align:center;">Total</th><th style="text-align:center;">Promedio</th></tr></thead><tbody>`;
    analytics.rankedAthletes.slice(0, 3).forEach((a, i) => {
      const name = a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim();
      html += `<tr>
        <td class="pdf-rank-cell"><span class="${medalClass[i]}">${medalIcon[i]}</span></td>
        <td><strong>${name}</strong></td>
        <td>${a.clubName}</td>
        <td><span class="pdf-level-badge">${a.level || '—'}</span></td>
        <td style="text-align:center;"><strong style="font-size:0.95rem;color:#6366f1;">${a.total}</strong></td>
        <td style="text-align:center;">${a.avg}</td>
      </tr>`;
    });
    html += `</tbody></table>`;
  }

  // Ranking completo
  html += `<div class="pdf-subtitle">Ranking Completo</div>`;
  html += `<table class="pdf-table"><thead><tr><th style="width:48px;text-align:center;">#</th><th>Gimnasta</th><th>Club</th><th>Nivel</th><th style="text-align:center;">Total</th><th style="text-align:center;">Promedio</th></tr></thead><tbody>`;
  analytics.rankedAthletes.forEach((a, i) => {
    const name = a.name || `${a.first_name || ''} ${a.last_name || ''}`.trim();
    const posHtml = i < 3
      ? `<span class="${medalClass[i]}">${medalIcon[i]}</span>`
      : (i + 1);
    html += `<tr>
      <td class="pdf-rank-cell">${posHtml}</td>
      <td>${name}</td>
      <td>${a.clubName}</td>
      <td><span class="pdf-level-badge">${a.level || '—'}</span></td>
      <td style="text-align:center;"><strong>${a.total}</strong></td>
      <td style="text-align:center;">${a.avg}</td>
    </tr>`;
  });
  html += `</tbody></table>`;

  // Promedio por club
  if (analytics.clubStats.length > 0) {
    html += `<div class="pdf-subtitle">🏛️ Promedio por Club</div>`;
    html += `<table class="pdf-table"><thead><tr><th>Club</th><th>Atletas</th><th>Promedio</th></tr></thead><tbody>`;
    analytics.clubStats.forEach(c => {
      html += `<tr><td><strong>${c.name}</strong></td><td>${c.athletes}</td><td><strong>${c.avg}</strong></td></tr>`;
    });
    html += `</tbody></table>`;
  }

  html += buildFooter();
  openPrintWindow(`Resultados — ${event.name}`, html);
}


// ===== 2. PDF DE CLUBES PARTICIPANTES =====
export function generateClubsPDF(event, clubs, accessRequests, clubSummary) {
  let html = buildHeader(event);

  const approvedClubs = (accessRequests || [])
    .filter(r => r.status === 'approved')
    .map(r => r.clubs || { id: r.club_id, name: 'Club', email: '', location: '' });

  const allClubs = clubs && clubs.length > 0 ? clubs : approvedClubs;

  // Build athlete count map from clubSummary
  const athleteCountMap = {};
  let totalAthletes = 0;
  if (clubSummary && clubSummary.length > 0) {
    clubSummary.forEach(cs => {
      if (cs.club?.id) {
        athleteCountMap[cs.club.id] = cs.count || 0;
        totalAthletes += cs.count || 0;
      }
    });
  }

  html += `
    <div class="pdf-stats">
      <div class="pdf-stat"><span class="pdf-stat-val">${allClubs.length}</span><span class="pdf-stat-label">Clubes Participantes</span></div>
      ${totalAthletes > 0 ? `<div class="pdf-stat"><span class="pdf-stat-val">${totalAthletes}</span><span class="pdf-stat-label">Atletas Inscritas</span></div>` : ''}
    </div>
  `;

  html += `<div class="pdf-subtitle">🏛️ Clubes Participantes</div>`;
  html += `<table class="pdf-table"><thead><tr><th>#</th><th>Club</th><th>Email</th><th>Ubicación</th><th>Atletas</th></tr></thead><tbody>`;
  allClubs.forEach((club, i) => {
    const count = athleteCountMap[club.id] ?? '—';
    html += `<tr><td>${i + 1}</td><td><strong>${club.name || '—'}</strong></td><td>${club.email || '—'}</td><td>${club.location || '—'}</td><td style="text-align:center; font-weight:600;">${count}</td></tr>`;
  });
  html += `</tbody></table>`;

  html += buildFooter();
  openPrintWindow(`Clubes Participantes — ${event.name}`, html);
}


// ===== 3. PDF DE CLUBES PARTICIPANTES CON ATLETAS =====
export function generateClubsWithAthletesPDF(event, clubSummary, clubLogos) {
  let html = buildHeader(event);

  const totalAthletes = clubSummary.reduce((sum, c) => sum + c.count, 0);
  const logoMap = clubLogos || {};

  html += `
    <div class="pdf-stats">
      <div class="pdf-stat"><span class="pdf-stat-val">${clubSummary.length}</span><span class="pdf-stat-label">Clubes</span></div>
      <div class="pdf-stat"><span class="pdf-stat-val">${totalAthletes}</span><span class="pdf-stat-label">Atletas Inscritas</span></div>
    </div>
  `;

  clubSummary.forEach((cs, idx) => {
    if (idx > 0 && idx % 3 === 0) {
      html += `</div><div class="page-break"></div>`;
      html += buildHeader(event);
    }

    const clubId = cs.club?.id;
    const logoUrl = clubId ? logoMap[clubId] : null;
    const logoHtml = logoUrl
      ? `<img src="${logoUrl}" style="width:28px;height:28px;border-radius:6px;object-fit:cover;vertical-align:middle;margin-right:8px;border:1px solid #e4e4e7;" crossorigin="anonymous" />`
      : '';
    html += `<div class="pdf-subtitle">${logoHtml}${cs.club?.name || 'Club'} (${cs.count} atletas)</div>`;
    html += `<table class="pdf-table"><thead><tr><th>#</th><th>Nombre</th><th>Nivel</th><th>Franja</th><th>Edad</th></tr></thead><tbody>`;
    cs.gymnasts.forEach((g, i) => {
      const age = calculateAge(g.birth_date) ?? '—';
      html += `<tr><td>${i + 1}</td><td>${g.name || '—'}</td><td><span class="pdf-level-badge">${g.level || '—'}</span></td><td>${g.franja || '—'}</td><td>${age}</td></tr>`;
    });
    html += `</tbody></table>`;
  });

  html += buildFooter();
  openPrintWindow(`Clubes y Atletas — ${event.name}`, html);
}


// ===== 4. PLANILLAS DE PUNTUACIÓN PARA JUECES =====
// Una hoja por club para un aparato específico.
// Atletas en orden alfabético, columnas vacías para que el juez titule,
// y columna final de Total.
export function generateJudgeScoreSheetsPDF(event, eventLevels, clubSummary, apparatusCode, filters = {}) {
  if (!clubSummary || clubSummary.length === 0) {
    const win = window.open('', '_blank');
    if (!win) { alert('Permite las ventanas emergentes para descargar PDFs'); return; }
    win.document.write(`<html><body style="font-family:sans-serif;padding:40px;text-align:center;"><h2>Sin datos</h2><p>No hay atletas inscritas para generar planillas.</p></body></html>`);
    win.document.close();
    return;
  }

  const appName = getApparatusDisplayName(apparatusCode, event.discipline, event.category);
  const filterLevel = filters.level || '';
  const filterFranja = filters.franja || '';
  const NUM_SCORE_COLS = 6; // columnas vacías para que el juez escriba
  let html = '';
  let pageCount = 0;

  // Build filter description for subtitle
  const filterParts = [];
  if (filterLevel) filterParts.push(filterLevel);
  if (filterFranja) filterParts.push(filterFranja);
  const filterLabel = filterParts.length > 0 ? ` — ${filterParts.join(' · ')}` : '';

  // Sort clubs alphabetically
  const sortedClubs = [...clubSummary].sort((a, b) =>
    (a.club?.name || '').localeCompare(b.club?.name || '')
  );

  sortedClubs.forEach(cs => {
    // Sort gymnasts alphabetically within each club, applying filters
    const gymnasts = [...(cs.gymnasts || [])]
      .filter(g => {
        if (filterLevel && g.level !== filterLevel) return false;
        if (filterFranja && g.franja !== filterFranja) return false;
        return true;
      })
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

    if (gymnasts.length === 0) return;

    if (pageCount > 0) {
      html += `</div><div class="page-break"></div>`;
    }
    pageCount++;

    // Header
    html += buildHeader(event);

    // Apparatus + Club info
    html += `<div class="pdf-subtitle">📋 ${appName}${filterLabel}</div>`;
    html += `<div class="pdf-club-header">
      <span class="pdf-club-name">🏛️ ${cs.club?.name || 'Club'}</span>
      <span class="pdf-club-count">${gymnasts.length} atleta${gymnasts.length !== 1 ? 's' : ''}</span>
    </div>`;

    // Table
    html += `<table class="pdf-table pdf-score-table"><thead><tr>`;
    html += `<th class="pdf-col-num">#</th>`;
    html += `<th class="pdf-col-name">Gimnasta</th>`;
    html += `<th class="pdf-col-level">Nivel</th>`;
    // Empty header cells for judges to write their own criteria
    for (let c = 0; c < NUM_SCORE_COLS; c++) {
      html += `<th class="pdf-col-score"></th>`;
    }
    html += `<th class="pdf-col-total">Total</th>`;
    html += `</tr></thead><tbody>`;

    gymnasts.forEach((g, i) => {
      html += `<tr>`;
      html += `<td class="pdf-cell-num">${i + 1}</td>`;
      html += `<td class="pdf-cell-name">${g.name || '—'}</td>`;
      html += `<td class="pdf-cell-level"><span class="pdf-level-badge">${g.level || '—'}</span></td>`;
      for (let c = 0; c < NUM_SCORE_COLS; c++) {
        html += `<td class="pdf-cell-score"></td>`;
      }
      html += `<td class="pdf-cell-total"></td>`;
      html += `</tr>`;
    });

    html += `</tbody></table>`;

    // Signature line
    html += `<div class="pdf-signature">
      <div class="pdf-signature-line"><span>Firma del Juez</span></div>
      <div class="pdf-signature-line"><span>Nombre del Juez</span></div>
    </div>`;

  });

  // Cerrar con un solo footer al final
  if (html) {
    html += buildFooter();
  } else {
    html = buildHeader(event);
    html += `<div class="pdf-empty">No se generaron planillas. No hay atletas inscritas.</div>`;
    html += buildFooter();
  }

  openPrintWindow(`Planilla ${appName}${filterLabel} — ${event.name}`, html);
}


function getApparatusDisplayName(apparatusCode, discipline, category) {
  // Try to find from EVENTS_BY_DISCIPLINE
  const disciplineEvents = {
    acrobatic: {
      women_pair: [{ code: 'balance', name: 'Ejercicio de Balance' }, { code: 'dynamic', name: 'Ejercicio Dinámico' }, { code: 'combined', name: 'Ejercicio Combinado' }],
      men_pair: [{ code: 'balance', name: 'Ejercicio de Balance' }, { code: 'dynamic', name: 'Ejercicio Dinámico' }, { code: 'combined', name: 'Ejercicio Combinado' }],
      mixed_pair: [{ code: 'balance', name: 'Ejercicio de Balance' }, { code: 'dynamic', name: 'Ejercicio Dinámico' }, { code: 'combined', name: 'Ejercicio Combinado' }],
    },
    artistic: {
      wag: [
        { code: 'vt', apparatus: 'salto', name: 'Salto' },
        { code: 'ub', apparatus: 'barras_asimetricas', name: 'Barras Asimétricas' },
        { code: 'bb', apparatus: 'viga', name: 'Viga de Equilibrio' },
        { code: 'fx', apparatus: 'suelo', name: 'Suelo' },
      ],
      mag: [
        { code: 'fx', apparatus: 'suelo', name: 'Suelo' },
        { code: 'ph', apparatus: 'caballo', name: 'Caballo con Arzones' },
        { code: 'sr', apparatus: 'anillas', name: 'Anillas' },
        { code: 'vt', apparatus: 'salto', name: 'Salto' },
        { code: 'pb', apparatus: 'barras_paralelas', name: 'Barras Paralelas' },
        { code: 'hb', apparatus: 'barra_fija', name: 'Barra Fija' },
      ]
    },
    rhythmic: {
      individual: [
        { code: 'hoop', apparatus: 'aro', name: 'Aro' },
        { code: 'ball', apparatus: 'pelota', name: 'Pelota' },
        { code: 'clubs', apparatus: 'mazas', name: 'Mazas' },
        { code: 'ribbon', apparatus: 'cinta', name: 'Cinta' },
      ]
    }
  };

  // Search across discipline events
  if (discipline && disciplineEvents[discipline]) {
    const cats = disciplineEvents[discipline];
    for (const cat of Object.values(cats)) {
      const found = cat.find(e => e.code === apparatusCode || e.apparatus === apparatusCode);
      if (found) return found.name;
    }
  }

  // Fallback mapping
  const fallback = {
    salto: 'Salto', viga: 'Viga de Equilibrio', suelo: 'Suelo',
    barras_asimetricas: 'Barras Asimétricas', barras_paralelas: 'Barras Paralelas',
    barra_fija: 'Barra Fija', caballo: 'Caballo con Arzones', anillas: 'Anillas',
    aro: 'Aro', pelota: 'Pelota', mazas: 'Mazas', cinta: 'Cinta',
    all_around: 'All Around', team: 'Equipo',
    balance: 'Ejercicio de Balance', dynamic: 'Ejercicio Dinámico', combined: 'Ejercicio Combinado',
    individual_m: 'Individual Masculino', individual_f: 'Individual Femenino',
    sync_m: 'Sincronizado Masculino', sync_f: 'Sincronizado Femenino',
    tumbling_m: 'Tumbling Masculino', tumbling_f: 'Tumbling Femenino',
    floor_teamgym: 'Suelo TeamGym', tumbling_teamgym: 'Tumbling TeamGym',
    mini_tramp_teamgym: 'Mini Trampolín TeamGym',
    speed_m: 'Speed Run Masculino', speed_f: 'Speed Run Femenino',
    freestyle_m: 'Freestyle Masculino', freestyle_f: 'Freestyle Femenino',
    display: 'Exhibición', '5_aros': '5 Aros', mix_cinta_pelota: '3 Cintas + 2 Pelotas',
    pairs: 'Parejas Mixtas', trios: 'Tríos', groups: 'Grupos',
    dmt_m: 'Doble Mini Masculino', dmt_f: 'Doble Mini Femenino'
  };

  return fallback[apparatusCode] || apparatusCode || 'Aparato';
}

// ===== CLUB PDFs =====

function buildClubHeader(clubName, subtitle, logoUrl, eyebrow = 'Reporte del Club') {
  const initials = (clubName || '?').split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" class="pdf-hero-logo" crossorigin="anonymous" />`
    : `<div class="pdf-hero-logo-placeholder">${initials || '🏟'}</div>`;
  return `
    <div class="pdf-hero">
      <span class="pdf-hero-brand">CopaGimnasta</span>
      <div class="pdf-hero-inner">
        ${logoHtml}
        <div class="pdf-hero-meta">
          <div class="pdf-hero-eyebrow">${eyebrow}</div>
          <div class="pdf-hero-title">${clubName || 'Club'}</div>
          <div class="pdf-hero-subtitle">${subtitle}</div>
        </div>
      </div>
    </div>
    <div class="pdf-body">
  `;
}

// ===== PDF: Calendario Semanal (formato visual de bloques) =====
export function generateWeeklyCalendarPDF(clubName, blocks, logoUrl) {
  const daysShort = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const daysFull = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const now = new Date();
  const subtitle = `Calendario Semanal — ${now.toLocaleDateString('es', { month: 'long', year: 'numeric' })}`;

  let html = buildClubHeader(clubName, subtitle, logoUrl);

  // --- Config: 08:00 → 22:00 en slots de 30 minutos ---
  const START_HOUR = 8;
  const END_HOUR = 22;
  const SLOT_MIN = 30;
  const slots = [];
  for (let h = START_HOUR; h < END_HOUR; h++) {
    slots.push(h * 60);
    slots.push(h * 60 + 30);
  }
  slots.push(END_HOUR * 60);
  const totalSlots = slots.length;
  const SLOT_HEIGHT = 15; // px por slot de 30min (compacto para caber en 1 hoja landscape)

  // --- Orden de días: Lun → Dom ---
  const dayOrder = [1, 2, 3, 4, 5, 6, 0];

  // --- Helpers ---
  const parseMin = (t) => {
    if (!t) return START_HOUR * 60;
    const [h, m] = t.split(':').map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const mmToSlotIndex = (mm) => Math.max(0, Math.round((mm - START_HOUR * 60) / SLOT_MIN));
  const getBlockDays = (b) => (
    Array.isArray(b.days_of_week) && b.days_of_week.length > 0
      ? b.days_of_week.map(Number)
      : (b.day_of_week != null ? [Number(b.day_of_week)] : [])
  );

  // --- Indexar bloques por día ---
  const blocksByDay = {};
  dayOrder.forEach(d => { blocksByDay[d] = []; });
  blocks.forEach(b => {
    const startMin = parseMin(b.start_time);
    const dur = b.duration_minutes || 60;
    getBlockDays(b).forEach(d => {
      if (blocksByDay[d] != null) {
        blocksByDay[d].push({ ...b, _startMin: startMin, _duration: dur });
      }
    });
  });

  // --- Grid visual ---
  const TIME_COL_W = 56;
  html += `<div style="border:1px solid #e2e8f0;border-radius:10px;overflow:hidden;background:white;">`;

  // Header de días
  html += `<div style="display:flex;background:#f1f5f9;border-bottom:2px solid #e2e8f0;">`;
  html += `<div style="width:${TIME_COL_W}px;flex-shrink:0;border-right:1px solid #e2e8f0;padding:8px 4px;font-size:0.64rem;color:#64748b;text-transform:uppercase;text-align:center;font-weight:700;">Hora</div>`;
  dayOrder.forEach((d, i) => {
    const isLast = i === dayOrder.length - 1;
    html += `<div style="flex:1;padding:8px 4px;text-align:center;font-size:0.74rem;font-weight:700;color:#0f172a;text-transform:uppercase;letter-spacing:0.5px;${isLast ? '' : 'border-right:1.5px solid #94a3b8;'}">${daysFull[d]}</div>`;
  });
  html += `</div>`;

  // Cuerpo: columna de horas (absoluta) + columnas de días
  const bodyHeight = (totalSlots - 1) * SLOT_HEIGHT;
  html += `<div style="display:flex;position:relative;">`;

  // Columna de horas
  html += `<div style="width:${TIME_COL_W}px;flex-shrink:0;border-right:2px solid #64748b;position:relative;height:${bodyHeight}px;">`;
  slots.forEach((mm, idx) => {
    if (idx === totalSlots - 1) return;
    const top = idx * SLOT_HEIGHT;
    const hh = Math.floor(mm / 60);
    const mn = mm % 60;
    const label = `${String(hh).padStart(2, '0')}:${String(mn).padStart(2, '0')}`;
    const isHour = mn === 0;
    html += `<div style="position:absolute;top:${top}px;left:0;right:0;height:${SLOT_HEIGHT}px;font-size:0.6rem;color:${isHour ? '#1e293b' : '#94a3b8'};font-weight:${isHour ? 700 : 500};text-align:right;padding-right:6px;border-top:${isHour ? '1.5px solid #94a3b8' : '1px dashed #cbd5e1'};box-sizing:border-box;line-height:${SLOT_HEIGHT - 2}px;">${isHour ? label : ''}</div>`;
  });
  html += `</div>`;

  // Columnas de días con bloques absolutos
  dayOrder.forEach((d, colIdx) => {
    const isLast = colIdx === dayOrder.length - 1;
    const dayBlocks = blocksByDay[d] || [];
    html += `<div style="flex:1;position:relative;height:${bodyHeight}px;${isLast ? '' : 'border-right:1.5px solid #94a3b8;'}">`;

    // Líneas guía horarias
    for (let s = 0; s < totalSlots - 1; s++) {
      const mm = slots[s];
      const top = s * SLOT_HEIGHT;
      const isHour = mm % 60 === 0;
      html += `<div style="position:absolute;top:${top}px;left:0;right:0;height:${SLOT_HEIGHT}px;border-top:${isHour ? '1.5px solid #94a3b8' : '1px dashed #cbd5e1'};box-sizing:border-box;"></div>`;
    }

    // Bloques
    dayBlocks.forEach(b => {
      const startIdx = mmToSlotIndex(b._startMin);
      const slotsSpan = Math.max(1, Math.round(b._duration / SLOT_MIN));
      const top = startIdx * SLOT_HEIGHT;
      const height = slotsSpan * SLOT_HEIGHT - 2;
      const color = b.color || '#8b5cf6';
      const count = b._athletes ? b._athletes.length : 0;
      const timeRange = `${(b.start_time || '').slice(0, 5)}-${(b.end_time || '').slice(0, 5)}`;

      // Contenido adaptado al alto disponible
      const showDuration = height >= 28;
      const showCount = height >= 42;
      const showAthletes = height >= 60 && count > 0 && count <= 5;
      const athletesHtml = showAthletes
        ? `<div style="font-size:0.52rem;opacity:0.92;margin-top:1px;line-height:1.25;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${b._athletes.slice(0, 5).map(a => a.name.split(' ')[0]).join(', ')}</div>`
        : '';

      html += `<div style="position:absolute;top:${top}px;left:2px;right:2px;height:${height}px;background:${color};color:white;border-radius:4px;padding:2px 5px;box-sizing:border-box;overflow:hidden;box-shadow:0 1px 2px rgba(0,0,0,0.15);border:1.5px solid rgba(0,0,0,0.18);">
        <div style="font-weight:700;font-size:0.65rem;line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${b.title || 'Bloque'}</div>
        ${showDuration ? `<div style="font-size:0.54rem;opacity:0.95;line-height:1.2;">${timeRange}</div>` : ''}
        ${showCount ? `<div style="font-size:0.54rem;opacity:0.9;line-height:1.2;">${count} atleta${count !== 1 ? 's' : ''}</div>` : ''}
        ${athletesHtml}
      </div>`;
    });

    html += `</div>`;
  });

  html += `</div></div>`;

  // --- Leyenda / Detalle de bloques ---
  if (blocks.length > 0) {
    html += `<div class="page-break"></div>`;
    html += `<h3 style="margin-top:18px;font-size:0.95rem;color:#0f172a;border-bottom:1px solid #e2e8f0;padding-bottom:6px;">Detalle de Bloques</h3>`;
    html += `<table class="pdf-table" style="margin-top:10px;">
      <thead>
        <tr>
          <th style="width:14px;"></th>
          <th>Bloque</th>
          <th>Días</th>
          <th style="text-align:center;">Horario</th>
          <th style="text-align:center;">Duración</th>
          <th style="text-align:center;">Atletas</th>
        </tr>
      </thead>
      <tbody>`;
    blocks.slice().sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')).forEach(b => {
      const ds = getBlockDays(b).map(d => daysShort[d]).join(', ') || '—';
      const count = b._athletes ? b._athletes.length : 0;
      html += `<tr>
        <td style="background:${b.color || '#8b5cf6'};"></td>
        <td><strong>${b.title || 'Bloque'}</strong></td>
        <td style="font-size:0.75rem;color:#475569;">${ds}</td>
        <td style="text-align:center;font-size:0.75rem;">${(b.start_time || '').slice(0, 5)} - ${(b.end_time || '').slice(0, 5)}</td>
        <td style="text-align:center;font-size:0.75rem;">${b.duration_minutes || '—'} min</td>
        <td style="text-align:center;"><span class="pdf-level-badge">${count}</span></td>
      </tr>`;
    });
    html += `</tbody></table>`;
  }

  html += buildFooter();
  openPrintWindow(`Calendario - ${clubName}`, html, { landscape: true });
}

// ===== PDF: Asistencia Diaria =====
export function generateDailyAttendancePDF(clubName, date, blocksWithAttendance, logoUrl) {
  const dateStr = new Date(date + 'T12:00').toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, `Asistencia del ${dateStr}`, logoUrl);

  if (blocksWithAttendance.length === 0) {
    html += `<p style="text-align:center;color:#94a3b8;padding:40px;">Sin bloques programados para este día</p>`;
  }

  blocksWithAttendance.forEach(block => {
    const bStart = (block.start_time || '').slice(0, 5);
    const bEnd = (block.end_time || '').slice(0, 5);
    const bColor = block.color || '#8b5cf6';
    const present = (block._attendanceAthletes || []).filter(a => a._status === 'present').length;
    const total = (block._attendanceAthletes || []).length;
    html += `<div class="pdf-block-row">
      <div class="pdf-subtitle" style="border-left-color:${bColor};">
        <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${bColor};margin-right:2px;"></span>
        ${block.title} (${bStart} - ${bEnd})
      </div>
      <div class="pdf-stat-sm">
        <span class="pdf-stat-sm-val">${present}/${total}</span>
        <span class="pdf-stat-sm-label">Presentes</span>
      </div>
    </div>`;
    html += `<table class="pdf-table"><thead><tr><th style="width:48px;text-align:center;">#</th><th>Atleta</th><th>Grupo</th><th style="text-align:center;">Estado</th><th style="text-align:center;">Justificado</th></tr></thead><tbody>`;
    (block._attendanceAthletes || []).forEach((a, i) => {
      const isPresent = a._status === 'present';
      const isJustified = a._status === 'justified';
      const statusLabel = isPresent
        ? '<span class="pdf-badge-present">PRESENTE</span>'
        : isJustified
          ? '<span class="pdf-badge-justified">JUSTIFICADO</span>'
          : '<span class="pdf-badge-absent">AUSENTE</span>';
      const justifiedLabel = isJustified ? '<span class="pdf-badge-justified">SÍ</span>' : '<span style="color:#cbd5e1;">—</span>';
      html += `<tr><td class="pdf-rank-cell">${i + 1}</td><td><strong>${a.name}</strong></td><td><span class="pdf-level-badge">${a.category || '—'}</span></td><td style="text-align:center;">${statusLabel}</td><td style="text-align:center;">${justifiedLabel}</td></tr>`;
    });
    html += `</tbody></table>`;
  });

  html += buildFooter();
  openPrintWindow(`Asistencia ${date} - ${clubName}`, html);
}

// ===== PDF: Asistencia Semanal =====
export function generateWeeklyAttendancePDF(clubName, weekStart, weekEnd, dailyData, logoUrl) {
  const ws = new Date(weekStart + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short' });
  const we = new Date(weekEnd + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
  let html = buildClubHeader(clubName, `Resumen de Asistencia — Semana ${ws} al ${we}`, logoUrl);

  // Group records by date
  const byDate = {};
  (dailyData || []).forEach(r => {
    if (!byDate[r.date]) byDate[r.date] = [];
    byDate[r.date].push(r);
  });
  const sortedDates = Object.keys(byDate).sort();

  // One section per day
  sortedDates.forEach((date, di) => {
    if (di > 0) html += '<div class="page-break"></div>';
    const dateLabel = new Date(date + 'T12:00').toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    const recs = byDate[date];
    const presentCount = recs.filter(r => r.status === 'present').length;
    const total = recs.length;
    html += '<div class="pdf-block-row">'
      + '<div class="pdf-subtitle" style="text-transform:capitalize;">' + dateLabel + '</div>'
      + '<div class="pdf-stat-sm"><span class="pdf-stat-sm-val">' + presentCount + '/' + total + '</span><span class="pdf-stat-sm-label">Presentes</span></div>'
      + '</div>';

    html += '<table class="pdf-table"><thead><tr><th style="width:48px;text-align:center;">#</th><th>Atleta</th><th>Grupo</th><th style="text-align:center;">Estado</th></tr></thead><tbody>';
    recs.sort((a, b) => (a.gimnasts?.name || '').localeCompare(b.gimnasts?.name || '')).forEach((r, i) => {
      const name = r.gimnasts?.name || '—';
      const cat = r.gimnasts?.category || '—';
      const statusLabel = r.status === 'present'
        ? '<span class="pdf-badge-present">PRESENTE</span>'
        : (r.status === 'justified' || r.status === 'rescheduled')
          ? '<span class="pdf-badge-justified">JUSTIFICADO</span>'
          : '<span class="pdf-badge-absent">AUSENTE</span>';
      html += '<tr><td class="pdf-rank-cell">' + (i + 1) + '</td><td><strong>' + name + '</strong></td><td><span class="pdf-level-badge">' + cat + '</span></td><td style="text-align:center;">' + statusLabel + '</td></tr>';
    });
    html += '</tbody></table>';
  });

  if (sortedDates.length === 0) {
    html += '<p style="text-align:center;color:#94a3b8;padding:40px;">Sin registros de asistencia en esta semana</p>';
  }

  html += buildFooter();
  openPrintWindow(`Asistencia Semanal - ${clubName}`, html);
}

// ===== PDF: Promedios Mensuales =====
export function generateMonthlyAveragesPDF(clubName, year, month, monthlyData, logoUrl) {
  const monthName = new Date(year, month - 1, 1).toLocaleDateString('es', { month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, `Promedios de Asistencia — ${monthName}`, logoUrl);

  // Build athlete stats
  const athleteMap = {};
  monthlyData.forEach(record => {
    const name = record.gimnasts?.name || 'Desconocido';
    const id = record.gymnast_id;
    const cat = record.gimnasts?.category || '';
    if (!athleteMap[id]) athleteMap[id] = { name, category: cat, present: 0, total: 0 };
    athleteMap[id].total++;
    if (record.status === 'present') athleteMap[id].present++;
  });

  const athletes = Object.values(athleteMap).sort((a, b) => a.name.localeCompare(b.name));

  // Group by category — show athletes within each group
  const byCategory = {};
  athletes.forEach(a => {
    const cat = a.category || 'Sin Grupo';
    if (!byCategory[cat]) byCategory[cat] = { present: 0, total: 0, athletes: [] };
    byCategory[cat].present += a.present;
    byCategory[cat].total += a.total;
    byCategory[cat].athletes.push(a);
  });

  Object.entries(byCategory).sort((a, b) => a[0].localeCompare(b[0])).forEach(([cat, data]) => {
    const groupPct = data.total > 0 ? Math.round((data.present / data.total) * 100) : 0;
    const groupColor = groupPct >= 80 ? '#16a34a' : groupPct >= 60 ? '#d97706' : '#dc2626';
    html += `<div class="pdf-group-header">
      <span class="pdf-group-header-title">${cat}</span>
      <span class="pdf-group-header-count">${data.athletes.length} atleta${data.athletes.length !== 1 ? 's' : ''}</span>
      <span class="pdf-group-header-pct" style="color:${groupColor};">${groupPct}%</span>
    </div>`;
    html += `<table class="pdf-table"><thead><tr><th style="width:48px;text-align:center;">#</th><th>Atleta</th><th style="text-align:center;">% Asistencia</th></tr></thead><tbody>`;
    data.athletes.sort((a, b) => a.name.localeCompare(b.name)).forEach((a, i) => {
      const pct = a.total > 0 ? Math.round((a.present / a.total) * 100) : 0;
      const color = pct >= 80 ? '#16a34a' : pct >= 60 ? '#d97706' : '#dc2626';
      html += `<tr><td class="pdf-rank-cell">${i + 1}</td><td><strong>${a.name}</strong></td><td style="text-align:center;font-weight:800;font-size:0.92rem;color:${color};">${pct}%</td></tr>`;
    });
    html += `</tbody></table>`;
  });

  html += buildFooter();
  openPrintWindow(`Promedios ${monthName} - ${clubName}`, html);
}

// ===== PDF: Asistencia Diaria Entrenadores =====
export function generateCoachDailyAttendancePDF(clubName, date, records, coaches, logoUrl) {
  const dateStr = new Date(date + 'T12:00').toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, `Asistencia Entrenadores — ${dateStr}`, logoUrl);

  const recMap = {};
  (records || []).forEach(r => { recMap[r.coach_id] = r; });

  const total = (coaches || []).length;
  const presentCount = (coaches || []).filter(c => recMap[c.id]?.status === 'present').length;
  const absentCount = (coaches || []).filter(c => recMap[c.id] && recMap[c.id].status !== 'present').length;
  const noRecord = total - presentCount - absentCount;

  html += '<div class="pdf-stats">';
  html += '<div class="pdf-stat"><span class="pdf-stat-val">' + presentCount + '/' + total + '</span><span class="pdf-stat-label">Presentes</span></div>';
  html += '<div class="pdf-stat"><span class="pdf-stat-val">' + absentCount + '</span><span class="pdf-stat-label">Ausentes</span></div>';
  if (noRecord > 0) html += '<div class="pdf-stat"><span class="pdf-stat-val">' + noRecord + '</span><span class="pdf-stat-label">Sin registro</span></div>';
  html += '</div>';

  html += '<table class="pdf-table"><thead><tr><th style="width:48px;text-align:center;">#</th><th>Entrenador</th><th style="text-align:center;">Estado</th><th style="text-align:center;">Entrada</th><th style="text-align:center;">Salida</th></tr></thead><tbody>';
  (coaches || []).forEach((c, i) => {
    const rec = recMap[c.id];
    const status = rec?.status === 'present'
      ? '<span class="pdf-badge-present">PRESENTE</span>'
      : rec
        ? '<span class="pdf-badge-absent">AUSENTE</span>'
        : '<span class="pdf-badge-none">SIN REGISTRO</span>';
    const checkIn = rec?.check_in_time ? new Date(rec.check_in_time).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '<span style="color:#cbd5e1;">—</span>';
    const checkOut = rec?.check_out_time ? new Date(rec.check_out_time).toLocaleTimeString('es', { hour: '2-digit', minute: '2-digit' }) : '<span style="color:#cbd5e1;">—</span>';
    html += '<tr><td class="pdf-rank-cell">' + (i + 1) + '</td><td><strong>' + c.name + '</strong></td><td style="text-align:center;">' + status + '</td><td style="text-align:center;font-family:monospace;font-weight:600;color:#6366f1;">' + checkIn + '</td><td style="text-align:center;font-family:monospace;font-weight:600;color:#6366f1;">' + checkOut + '</td></tr>';
  });
  html += '</tbody></table>';

  html += buildFooter();
  openPrintWindow('Asistencia Entrenadores ' + date + ' - ' + clubName, html);
}

// ===== PDF: Asistencia Semanal Entrenadores =====
export function generateCoachWeeklyAttendancePDF(clubName, weekStart, weekEnd, records, logoUrl) {
  const ws = new Date(weekStart + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short' });
  const we = new Date(weekEnd + 'T12:00').toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' });
  let html = buildClubHeader(clubName, 'Asistencia Entrenadores — Semana ' + ws + ' al ' + we, logoUrl);

  // Collect all unique dates in the week and build coach map
  const allDates = [...new Set((records || []).map(r => r.date))].sort();
  const coachMap = {};
  (records || []).forEach(r => {
    const name = r.coaches?.name || 'Desconocido';
    const id = r.coach_id;
    if (!coachMap[id]) coachMap[id] = { name, dates: {}, present: 0, absent: 0, total: 0 };
    coachMap[id].total++;
    coachMap[id].dates[r.date] = r.status;
    if (r.status === 'present') coachMap[id].present++;
    else coachMap[id].absent++;
  });

  const coachList = Object.values(coachMap).sort((a, b) => a.name.localeCompare(b.name));
  const dayHeaders = allDates.map(d => new Date(d + 'T12:00').toLocaleDateString('es', { weekday: 'short', day: 'numeric' }));

  // Table with date columns
  html += '<table class="pdf-table"><thead><tr><th style="width:48px;text-align:center;">#</th><th>Entrenador</th>';
  dayHeaders.forEach(dh => { html += '<th style="text-align:center;font-size:0.62rem;">' + dh + '</th>'; });
  html += '<th style="text-align:center;">%</th></tr></thead><tbody>';
  coachList.forEach((c, i) => {
    const pct = c.total > 0 ? Math.round((c.present / c.total) * 100) : 0;
    const color = pct >= 80 ? '#16a34a' : pct >= 60 ? '#d97706' : '#dc2626';
    html += '<tr><td class="pdf-rank-cell">' + (i + 1) + '</td><td><strong>' + c.name + '</strong></td>';
    allDates.forEach(d => {
      const st = c.dates[d];
      const cell = st === 'present'
        ? '<span style="color:#16a34a;font-weight:800;">✔</span>'
        : st
          ? '<span style="color:#dc2626;font-weight:800;">✘</span>'
          : '<span style="color:#cbd5e1;">—</span>';
      html += '<td style="text-align:center;">' + cell + '</td>';
    });
    html += '<td style="text-align:center;font-weight:800;font-size:0.88rem;color:' + color + ';">' + pct + '%</td></tr>';
  });
  html += '</tbody></table>';

  html += buildFooter();
  openPrintWindow('Asistencia Entrenadores Semanal - ' + clubName, html);
}

// ===== PDF: Asistencia Mensual Entrenadores =====
export function generateCoachMonthlyAttendancePDF(clubName, year, month, records, logoUrl) {
  const monthName = new Date(year, month - 1, 1).toLocaleDateString('es', { month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, 'Asistencia Entrenadores — ' + monthName, logoUrl);

  // Collect all unique dates and build coach map
  const allDates = [...new Set((records || []).map(r => r.date))].sort();
  const coachMap = {};
  (records || []).forEach(r => {
    const name = r.coaches?.name || 'Desconocido';
    const id = r.coach_id;
    if (!coachMap[id]) coachMap[id] = { name, dates: {}, present: 0, absent: 0, total: 0 };
    coachMap[id].total++;
    coachMap[id].dates[r.date] = r.status;
    if (r.status === 'present') coachMap[id].present++;
    else coachMap[id].absent++;
  });

  const coachList = Object.values(coachMap).sort((a, b) => a.name.localeCompare(b.name));
  const totalCoaches = coachList.length;
  const avgPct = coachList.length > 0 ? Math.round(coachList.reduce((sum, c) => sum + (c.total > 0 ? (c.present / c.total) * 100 : 0), 0) / coachList.length) : 0;

  html += '<div class="pdf-stats">';
  html += '<div class="pdf-stat"><span class="pdf-stat-val">' + totalCoaches + '</span><span class="pdf-stat-label">Entrenadores</span></div>';
  html += '<div class="pdf-stat"><span class="pdf-stat-val">' + allDates.length + '</span><span class="pdf-stat-label">Días Registrados</span></div>';
  html += '<div class="pdf-stat"><span class="pdf-stat-val">' + avgPct + '%</span><span class="pdf-stat-label">Asistencia Promedio</span></div>';
  html += '</div>';

  // Day number headers
  const dayHeaders = allDates.map(d => parseInt(d.split('-')[2]));

  html += '<div style="overflow-x:auto;">';
  html += '<table class="pdf-table" style="font-size:0.7rem;"><thead><tr><th style="width:36px;text-align:center;">#</th><th>Entrenador</th>';
  dayHeaders.forEach(dh => { html += '<th style="text-align:center;padding:3px 2px;font-size:0.6rem;min-width:18px;">' + dh + '</th>'; });
  html += '<th style="text-align:center;">%</th></tr></thead><tbody>';
  coachList.forEach((c, i) => {
    const pct = c.total > 0 ? Math.round((c.present / c.total) * 100) : 0;
    const color = pct >= 80 ? '#16a34a' : pct >= 60 ? '#d97706' : '#dc2626';
    html += '<tr><td class="pdf-rank-cell" style="font-size:0.68rem;">' + (i + 1) + '</td><td style="white-space:nowrap;"><strong>' + c.name + '</strong></td>';
    allDates.forEach(d => {
      const st = c.dates[d];
      const cell = st === 'present'
        ? '<span style="color:#16a34a;font-weight:800;">✔</span>'
        : st
          ? '<span style="color:#dc2626;font-weight:800;">✘</span>'
          : '';
      html += '<td style="text-align:center;padding:3px 1px;">' + cell + '</td>';
    });
    html += '<td style="text-align:center;font-weight:800;color:' + color + ';">' + pct + '%</td></tr>';
  });
  html += '</tbody></table></div>';

  html += buildFooter();
  openPrintWindow('Asistencia Entrenadores ' + monthName + ' - ' + clubName, html);
}

// ===== PDF: QR General de Asistencia de Entrenadores =====
export function generateCoachQRPDF(clubName, qrUrl, logoUrl) {
  const initials = (clubName || '?').split(' ').map(w => w[0]).filter(Boolean).join('').toUpperCase().slice(0, 2);
  const logoHtml = logoUrl
    ? `<img src="${logoUrl}" style="width:88px;height:88px;border-radius:18px;object-fit:cover;border:3px solid rgba(255,255,255,0.3);box-shadow:0 8px 24px rgba(0,0,0,0.2);" crossorigin="anonymous" />`
    : `<div style="width:88px;height:88px;border-radius:18px;display:flex;align-items:center;justify-content:center;background:rgba(255,255,255,0.15);color:#fff;font-weight:800;font-size:2rem;border:3px solid rgba(255,255,255,0.3);">${initials || '🏟'}</div>`;

  const html = `
    <style>
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body {
        font-family: 'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif;
        color: #0f172a;
        background: #fff;
      }
      .qr-hero {
        background: linear-gradient(135deg, #1e1b4b 0%, #4c1d95 45%, #7c3aed 100%);
        color: #fff;
        padding: 36px 28px 44px;
        text-align: center;
        position: relative;
        overflow: hidden;
      }
      .qr-hero::before {
        content: '';
        position: absolute;
        top: -30%; right: -10%;
        width: 320px; height: 320px;
        background: radial-gradient(circle, rgba(251,191,36,0.3) 0%, rgba(251,191,36,0) 70%);
        pointer-events: none;
      }
      .qr-hero::after {
        content: '';
        position: absolute;
        bottom: 0; left: 0; right: 0;
        height: 5px;
        background: linear-gradient(90deg, #fbbf24 0%, #f59e0b 30%, #ec4899 60%, #7c3aed 100%);
      }
      .qr-brand {
        position: absolute;
        top: 14px; right: 22px;
        font-size: 0.68rem;
        color: rgba(255,255,255,0.6);
        font-weight: 700;
        letter-spacing: 2px;
        text-transform: uppercase;
      }
      .qr-eyebrow {
        display: inline-block;
        padding: 4px 14px;
        background: rgba(251,191,36,0.2);
        color: #fde68a;
        border-radius: 4px;
        font-size: 0.7rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        margin: 18px 0 8px;
      }
      .qr-club-name {
        font-size: 1.9rem;
        font-weight: 800;
        letter-spacing: -0.02em;
        margin: 4px 0 6px;
      }
      .qr-subtitle {
        font-size: 0.92rem;
        color: rgba(255,255,255,0.8);
        font-weight: 500;
      }

      .qr-card-wrap {
        padding: 40px 24px 24px;
        text-align: center;
      }
      .qr-card {
        display: inline-block;
        background: #fff;
        border-radius: 24px;
        padding: 28px;
        box-shadow: 0 12px 40px rgba(124,58,237,0.18), 0 2px 8px rgba(15,23,42,0.08);
        border: 1px solid #e2e8f0;
        position: relative;
      }
      .qr-card::before {
        content: '';
        position: absolute;
        inset: -3px;
        border-radius: 26px;
        padding: 3px;
        background: linear-gradient(135deg, #7c3aed 0%, #ec4899 50%, #fbbf24 100%);
        -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
        -webkit-mask-composite: xor;
        mask-composite: exclude;
        z-index: -1;
      }
      .qr-img { width: 300px; height: 300px; display: block; }

      .qr-cta {
        margin: 28px auto 0;
        max-width: 420px;
      }
      .qr-cta-title {
        font-size: 1.15rem;
        font-weight: 800;
        color: #0f172a;
        margin-bottom: 6px;
        letter-spacing: -0.01em;
      }
      .qr-cta-desc {
        font-size: 0.88rem;
        color: #64748b;
        line-height: 1.5;
      }
      .qr-steps {
        display: flex;
        gap: 8px;
        justify-content: center;
        margin: 22px auto 0;
        max-width: 480px;
      }
      .qr-step {
        flex: 1;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 10px;
        padding: 10px 8px;
        font-size: 0.72rem;
        color: #475569;
      }
      .qr-step-num {
        display: inline-block;
        width: 22px; height: 22px;
        line-height: 22px;
        border-radius: 50%;
        background: #7c3aed;
        color: #fff;
        font-weight: 800;
        font-size: 0.72rem;
        margin-bottom: 4px;
      }
      .qr-step-text { display: block; font-weight: 600; color: #0f172a; font-size: 0.75rem; }

      .qr-footer-accent {
        width: 100%;
        height: 4px;
        background: linear-gradient(90deg, #fbbf24 0%, #f59e0b 30%, #ec4899 60%, #7c3aed 100%);
        margin-top: 40px;
      }
      .qr-footer {
        padding: 14px 28px;
        background: #0f172a;
        color: #94a3b8;
        font-size: 0.68rem;
        text-align: center;
        letter-spacing: 1px;
        text-transform: uppercase;
      }
      .qr-footer strong {
        color: #fde68a;
        font-weight: 800;
      }

      @media print {
        body { margin: 0; }
        @page { size: A4 portrait; margin: 0; }
      }
    </style>
    <div class="qr-hero">
      <span class="qr-brand">CopaGimnasta</span>
      ${logoHtml}
      <div class="qr-eyebrow">Control de Asistencia</div>
      <h1 class="qr-club-name">${clubName || 'Club'}</h1>
      <p class="qr-subtitle">Registro digital por código QR — Entrenadores</p>
    </div>

    <div class="qr-card-wrap">
      <div class="qr-card">
        <img class="qr-img" src="https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrUrl)}&color=0f172a" alt="QR Asistencia" />
      </div>

      <div class="qr-cta">
        <div class="qr-cta-title">📱 Escanea para fichar</div>
        <div class="qr-cta-desc">Usa la cámara de tu celular o una app lectora de QR para registrar tu entrada y salida.</div>
      </div>

      <div class="qr-steps">
        <div class="qr-step">
          <span class="qr-step-num">1</span>
          <span class="qr-step-text">Abre la cámara</span>
        </div>
        <div class="qr-step">
          <span class="qr-step-num">2</span>
          <span class="qr-step-text">Enfoca el QR</span>
        </div>
        <div class="qr-step">
          <span class="qr-step-num">3</span>
          <span class="qr-step-text">Confirma tu ingreso</span>
        </div>
      </div>
    </div>

    <div class="qr-footer-accent"></div>
    <div class="qr-footer"><strong>CopaGimnasta</strong> · Sistema de Asistencia por QR</div>
  `;

  const win = window.open('', '_blank');
  if (!win) { alert('Por favor permite las ventanas emergentes para descargar PDFs'); return; }
  win.document.write(`<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8"><title>QR Asistencia - ${clubName}</title></head><body>${html}</body></html>`);
  win.document.close();
  setTimeout(() => { win.focus(); win.print(); }, 800);
}

// ===== Helpers financieros =====
const fmtCLP = (n) => 'CLP $ ' + Number(n || 0).toLocaleString('es-CL');
const paymentMethodLabel = (m) => {
  const map = { efectivo: 'Efectivo', transferencia: 'Transferencia', yape: 'Yape', plin: 'Plin', bcp: 'BCP', bbva: 'BBVA', otro: 'Otro' };
  return map[m] || (m || '—');
};

// ===== PDF: Pagos del Día =====
export function generatePagosDiaPDF(clubName, date, pagos, logoUrl) {
  const dateStr = new Date(date + 'T12:00').toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, `Pagos del ${dateStr}`, logoUrl);

  const total = pagos.reduce((s, p) => s + Number(p.amount || 0), 0);
  const byMethod = {};
  pagos.forEach(p => {
    const k = p.payment_method || 'otro';
    byMethod[k] = (byMethod[k] || 0) + Number(p.amount || 0);
  });

  html += `<div class="pdf-block-row">
    <div class="pdf-subtitle">Resumen del Día</div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${pagos.length}</span><span class="pdf-stat-sm-label">Pagos</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${fmtCLP(total)}</span><span class="pdf-stat-sm-label">Total</span></div>
  </div>`;

  if (pagos.length === 0) {
    html += `<p style="text-align:center;color:#94a3b8;padding:40px;">Sin pagos registrados en esta fecha</p>`;
  } else {
    html += `<table class="pdf-table"><thead><tr><th style="width:40px;text-align:center;">#</th><th>Atleta</th><th>Plan</th><th>Método</th><th style="text-align:right;">Monto</th></tr></thead><tbody>`;
    pagos.forEach((p, i) => {
      const name = p.gimnasts?.name || '—';
      const plan = p.club_planes?.name || '—';
      html += `<tr><td class="pdf-rank-cell">${i + 1}</td><td><strong>${name}</strong></td><td>${plan}</td><td>${paymentMethodLabel(p.payment_method)}</td><td style="text-align:right;font-weight:600;">${fmtCLP(p.amount)}</td></tr>`;
    });
    html += `<tr style="background:#f1f5f9;font-weight:700;"><td colspan="4" style="text-align:right;">TOTAL</td><td style="text-align:right;">${fmtCLP(total)}</td></tr>`;
    html += `</tbody></table>`;

    if (Object.keys(byMethod).length > 0) {
      html += `<div class="pdf-subtitle" style="margin-top:18px;">Desglose por Método de Pago</div>`;
      html += `<table class="pdf-table"><thead><tr><th>Método</th><th style="text-align:right;">Monto</th></tr></thead><tbody>`;
      Object.entries(byMethod).forEach(([method, amt]) => {
        html += `<tr><td>${paymentMethodLabel(method)}</td><td style="text-align:right;font-weight:600;">${fmtCLP(amt)}</td></tr>`;
      });
      html += `</tbody></table>`;
    }
  }

  html += buildFooter();
  openPrintWindow(`Pagos ${date} - ${clubName}`, html);
}

// ===== PDF: Mensualidades (pagos del mes) =====
export function generateMensualidadesPDF(clubName, year, month, pagos, logoUrl) {
  const monthName = new Date(year, month - 1, 1).toLocaleDateString('es', { month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, `Mensualidades — ${monthName}`, logoUrl);

  const total = pagos.reduce((s, p) => s + Number(p.amount || 0), 0);

  // Agrupar por atleta
  const byAthlete = {};
  pagos.forEach(p => {
    const gid = p.gymnast_id || p.gimnasts?.id || 'unknown';
    if (!byAthlete[gid]) byAthlete[gid] = { name: p.gimnasts?.name || '—', plan: p.club_planes?.name || '—', pagos: [], total: 0 };
    byAthlete[gid].pagos.push(p);
    byAthlete[gid].total += Number(p.amount || 0);
  });

  html += `<div class="pdf-block-row">
    <div class="pdf-subtitle">Resumen del Mes</div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${pagos.length}</span><span class="pdf-stat-sm-label">Pagos</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${Object.keys(byAthlete).length}</span><span class="pdf-stat-sm-label">Atletas</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${fmtCLP(total)}</span><span class="pdf-stat-sm-label">Total</span></div>
  </div>`;

  if (pagos.length === 0) {
    html += `<p style="text-align:center;color:#94a3b8;padding:40px;">Sin pagos registrados en este mes</p>`;
  } else {
    html += `<table class="pdf-table"><thead><tr><th style="width:40px;text-align:center;">#</th><th>Atleta</th><th>Plan</th><th style="text-align:center;">Pagos</th><th style="text-align:right;">Total</th></tr></thead><tbody>`;
    Object.values(byAthlete)
      .sort((a, b) => a.name.localeCompare(b.name))
      .forEach((a, i) => {
        html += `<tr><td class="pdf-rank-cell">${i + 1}</td><td><strong>${a.name}</strong></td><td>${a.plan}</td><td style="text-align:center;">${a.pagos.length}</td><td style="text-align:right;font-weight:600;">${fmtCLP(a.total)}</td></tr>`;
      });
    html += `<tr style="background:#f1f5f9;font-weight:700;"><td colspan="4" style="text-align:right;">TOTAL DEL MES</td><td style="text-align:right;">${fmtCLP(total)}</td></tr>`;
    html += `</tbody></table>`;
  }

  html += buildFooter();
  openPrintWindow(`Mensualidades ${monthName} - ${clubName}`, html);
}

// ===== PDF: Membresías Activas =====
export function generateMembresiasPDF(clubName, memberships, logoUrl) {
  const now = new Date().toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, `Membresías — ${now}`, logoUrl);

  const active = memberships.filter(m => m.status === 'active');
  const totalMonthly = active.reduce((s, m) => s + Number(m.club_planes?.price || 0), 0);

  // Agrupar por plan
  const byPlan = {};
  active.forEach(m => {
    const pid = m.plan_id || 'sin-plan';
    const pname = m.club_planes?.name || 'Sin plan';
    if (!byPlan[pid]) byPlan[pid] = { name: pname, count: 0, price: m.club_planes?.price || 0, total: 0 };
    byPlan[pid].count += 1;
    byPlan[pid].total += Number(m.club_planes?.price || 0);
  });

  html += `<div class="pdf-block-row">
    <div class="pdf-subtitle">Resumen</div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${active.length}</span><span class="pdf-stat-sm-label">Activas</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${memberships.length - active.length}</span><span class="pdf-stat-sm-label">Inactivas</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${fmtCLP(totalMonthly)}</span><span class="pdf-stat-sm-label">Ingreso Potencial</span></div>
  </div>`;

  if (Object.keys(byPlan).length > 0) {
    html += `<div class="pdf-subtitle" style="margin-top:16px;">Distribución por Plan</div>`;
    html += `<table class="pdf-table"><thead><tr><th>Plan</th><th style="text-align:center;">Atletas</th><th style="text-align:right;">Precio</th><th style="text-align:right;">Total Mensual</th></tr></thead><tbody>`;
    Object.values(byPlan)
      .sort((a, b) => b.count - a.count)
      .forEach(p => {
        html += `<tr><td><strong>${p.name}</strong></td><td style="text-align:center;">${p.count}</td><td style="text-align:right;">${fmtCLP(p.price)}</td><td style="text-align:right;font-weight:600;">${fmtCLP(p.total)}</td></tr>`;
      });
    html += `</tbody></table>`;
  }

  if (active.length === 0) {
    html += `<p style="text-align:center;color:#94a3b8;padding:40px;">Sin membresías activas</p>`;
  } else {
    html += `<div class="pdf-subtitle" style="margin-top:18px;">Listado de Atletas</div>`;
    html += `<table class="pdf-table"><thead><tr><th style="width:40px;text-align:center;">#</th><th>Atleta</th><th>Plan</th><th style="text-align:center;">Frecuencia</th><th style="text-align:right;">Precio</th></tr></thead><tbody>`;
    active
      .slice()
      .sort((a, b) => (a.gimnasts?.name || '').localeCompare(b.gimnasts?.name || ''))
      .forEach((m, i) => {
        const name = m.gimnasts?.name || '—';
        const plan = m.club_planes?.name || '—';
        const freq = m.club_planes?.frequency || '—';
        const price = m.club_planes?.price || 0;
        html += `<tr><td class="pdf-rank-cell">${i + 1}</td><td><strong>${name}</strong></td><td>${plan}</td><td style="text-align:center;text-transform:capitalize;">${freq}</td><td style="text-align:right;font-weight:600;">${fmtCLP(price)}</td></tr>`;
      });
    html += `</tbody></table>`;
  }

  html += buildFooter();
  openPrintWindow(`Membresías - ${clubName}`, html);
}

// ===== PDF: Ejecutivo de Analítica IA =====
export function generateAnalyticsExecutivePDF(clubName, analyticsData, temporalTrend, logoUrl) {
  const now = new Date().toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, `Reporte Ejecutivo de Analítica — ${now}`, logoUrl);

  // KPIs globales
  html += `<div class="pdf-block-row">
    <div class="pdf-subtitle">Indicadores Globales</div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${analyticsData.globalAvg}</span><span class="pdf-stat-sm-label">Promedio</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${analyticsData.globalMax}</span><span class="pdf-stat-sm-label">Máximo</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${analyticsData.totalScores}</span><span class="pdf-stat-sm-label">Puntajes</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${analyticsData.athletePerf.length}</span><span class="pdf-stat-sm-label">Atletas</span></div>
  </div>`;

  // Promedio por aparato
  if (analyticsData.apparatusAvg?.length > 0) {
    html += `<div class="pdf-subtitle" style="margin-top:18px;">Promedio por Aparato</div>`;
    html += `<table class="pdf-table"><thead><tr><th>Aparato</th><th style="text-align:center;">Promedio</th><th style="text-align:center;">Máx.</th><th style="text-align:center;">Mín.</th><th style="text-align:center;">#</th></tr></thead><tbody>`;
    analyticsData.apparatusAvg
      .slice()
      .sort((a, b) => b.average - a.average)
      .forEach(ap => {
        const color = ap.average >= 8 ? '#16a34a' : ap.average >= 6 ? '#f59e0b' : '#dc2626';
        html += `<tr><td><strong>${ap.apparatus}</strong></td><td style="text-align:center;font-weight:700;color:${color};">${ap.average}</td><td style="text-align:center;">${ap.max}</td><td style="text-align:center;">${ap.min}</td><td style="text-align:center;">${ap.count}</td></tr>`;
      });
    html += `</tbody></table>`;
  }

  // Top 10 atletas
  if (analyticsData.athletePerf?.length > 0) {
    html += `<div class="pdf-subtitle" style="margin-top:18px;">Top 10 Atletas</div>`;
    html += `<table class="pdf-table"><thead><tr><th style="width:40px;text-align:center;">#</th><th>Atleta</th><th style="text-align:center;">Promedio</th><th style="text-align:center;">Total</th><th style="text-align:center;">Puntajes</th></tr></thead><tbody>`;
    analyticsData.athletePerf.slice(0, 10).forEach((a, i) => {
      html += `<tr><td class="pdf-rank-cell">${i + 1}</td><td><strong>${a.name}</strong></td><td style="text-align:center;font-weight:700;">${a.avg}</td><td style="text-align:center;">${a.total}</td><td style="text-align:center;">${a.count}</td></tr>`;
    });
    html += `</tbody></table>`;
  }

  // Comparativa por nivel
  if (analyticsData.levelComparison?.length > 0) {
    html += `<div class="pdf-subtitle" style="margin-top:18px;">Comparativa por Nivel</div>`;
    html += `<table class="pdf-table"><thead><tr><th>Nivel</th><th style="text-align:center;">Atletas</th><th style="text-align:center;">Prom.</th><th style="text-align:center;">Máx.</th><th style="text-align:center;">Mín.</th></tr></thead><tbody>`;
    analyticsData.levelComparison.forEach(lv => {
      html += `<tr><td><strong>${lv.level}</strong></td><td style="text-align:center;">${lv.count}</td><td style="text-align:center;font-weight:700;">${lv.levelAvg}</td><td style="text-align:center;">${lv.max}</td><td style="text-align:center;">${lv.min}</td></tr>`;
    });
    html += `</tbody></table>`;
  }

  // Tendencia temporal
  if (temporalTrend?.length > 0) {
    html += `<div class="pdf-subtitle" style="margin-top:18px;">Tendencia por Copa</div>`;
    html += `<table class="pdf-table"><thead><tr><th>Copa</th><th style="text-align:center;">Fecha</th><th style="text-align:center;">Promedio</th><th style="text-align:center;">Máx.</th><th style="text-align:center;">Atletas</th></tr></thead><tbody>`;
    temporalTrend.forEach(ev => {
      html += `<tr><td><strong>${ev.name}</strong></td><td style="text-align:center;">${ev.dateLabel}</td><td style="text-align:center;font-weight:700;">${ev.avg}</td><td style="text-align:center;">${ev.max}</td><td style="text-align:center;">${ev.athletes}</td></tr>`;
    });
    html += `</tbody></table>`;
  }

  // Alertas
  if (analyticsData.riskAlerts?.length > 0) {
    html += `<div class="page-break"></div>`;
    html += `<div class="pdf-subtitle" style="margin-top:18px;">Alertas IA — Áreas de Atención</div>`;
    html += `<table class="pdf-table"><thead><tr><th>Atleta / Aparato</th><th>Severidad</th><th>Observación</th></tr></thead><tbody>`;
    const sevOrder = { high: 0, mid: 1, low: 2 };
    analyticsData.riskAlerts
      .slice()
      .sort((a, b) => (sevOrder[a.severity] ?? 3) - (sevOrder[b.severity] ?? 3))
      .forEach(r => {
        const sevLabel = r.severity === 'high'
          ? '<span class="pdf-badge-absent">ALTA</span>'
          : r.severity === 'mid'
            ? '<span class="pdf-badge-justified">MEDIA</span>'
            : '<span class="pdf-level-badge">BAJA</span>';
        html += `<tr><td><strong>${r.name}</strong></td><td style="text-align:center;">${sevLabel}</td><td>${r.message}</td></tr>`;
      });
    html += `</tbody></table>`;
  }

  html += buildFooter();
  openPrintWindow(`Analítica Ejecutiva - ${clubName}`, html);
}

// ===== PDF: Deudores =====
export function generateDeudoresPDF(clubName, alertas, logoUrl) {
  const now = new Date().toLocaleDateString('es', { day: 'numeric', month: 'long', year: 'numeric' });
  let html = buildClubHeader(clubName, `Atletas con Deuda — ${now}`, logoUrl);

  const totalDeuda = alertas.reduce((s, a) => s + Number(a.plan?.price || 0), 0);
  const overdue = alertas.filter(a => a.is_overdue);

  html += `<div class="pdf-block-row">
    <div class="pdf-subtitle">Resumen</div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${alertas.length}</span><span class="pdf-stat-sm-label">Con Alerta</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${overdue.length}</span><span class="pdf-stat-sm-label">Vencidas</span></div>
    <div class="pdf-stat-sm"><span class="pdf-stat-sm-val">${fmtCLP(totalDeuda)}</span><span class="pdf-stat-sm-label">Monto Adeudado</span></div>
  </div>`;

  if (alertas.length === 0) {
    html += `<p style="text-align:center;color:#16a34a;padding:40px;font-weight:600;">¡Todo al día! No hay atletas con deuda.</p>`;
  } else {
    html += `<table class="pdf-table"><thead><tr><th style="width:40px;text-align:center;">#</th><th>Atleta</th><th>Plan</th><th style="text-align:center;">Días sin pagar</th><th style="text-align:center;">Estado</th><th style="text-align:right;">Monto</th></tr></thead><tbody>`;
    alertas
      .slice()
      .sort((a, b) => (b.days_since_payment || 0) - (a.days_since_payment || 0))
      .forEach((a, i) => {
        const name = a.gymnast?.name || '—';
        const plan = a.plan?.name || '—';
        const days = a.days_since_payment || 0;
        const price = a.plan?.price || 0;
        const badge = a.is_overdue
          ? '<span class="pdf-badge-absent">VENCIDO</span>'
          : '<span class="pdf-badge-justified">POR VENCER</span>';
        html += `<tr><td class="pdf-rank-cell">${i + 1}</td><td><strong>${name}</strong></td><td>${plan}</td><td style="text-align:center;font-weight:600;color:${a.is_overdue ? '#dc2626' : '#f59e0b'};">${days}</td><td style="text-align:center;">${badge}</td><td style="text-align:right;font-weight:600;">${fmtCLP(price)}</td></tr>`;
      });
    html += `<tr style="background:#fef2f2;font-weight:700;"><td colspan="5" style="text-align:right;">TOTAL ADEUDADO</td><td style="text-align:right;color:#dc2626;">${fmtCLP(totalDeuda)}</td></tr>`;
    html += `</tbody></table>`;
  }

  html += buildFooter();
  openPrintWindow(`Deudores - ${clubName}`, html);
}
